import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { get } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Section } from "@/components/common/PageHeader";
import { KpiCard } from "@/components/common/KpiCard";
import { Skeleton } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { IrisBrand } from "@/components/common/IrisBrand";
import { money, HEALTH_DOT } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  Wallet, Target, Trophy, KanbanSquare, Building2, ListTodo, X, Sparkles,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid,
} from "recharts";

const CHART = ["#2563EB", "#22C55E", "#F59E0B", "#8B5CF6", "#0EA5E9", "#EC4899"];

export default function Dashboard() {
  const { me } = useApp();
  const { data, isLoading } = useQuery({ queryKey: ["dashboard"], queryFn: () => get("/dashboard") });
  const [digestOpen, setDigestOpen] = useState(true);

  if (isLoading || !data) {
    return (
      <div className="grid grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-28" />)}
      </div>
    );
  }
  const k = data.kpis;

  return (
    <div>
      <PageHeader title={`Welcome back, ${me?.name?.split(" ")[0] || ""}`}
        subtitle="Here's what's happening across your workspace today." />

      {digestOpen && (
        <div data-testid="what-changed-digest"
          className="mb-6 flex items-start gap-3 rounded-xl border border-primary/20 bg-primary/5 p-4">
          <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-foreground">
              {data.what_changed.length} things changed since you were last here
            </p>
            <ul className="mt-1.5 space-y-1">
              {data.what_changed.map((w, i) => (
                <li key={i} className="text-[13px] text-muted-foreground">• {w.text}</li>
              ))}
            </ul>
          </div>
          <button data-testid="dismiss-digest" onClick={() => setDigestOpen(false)}
            className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
        <KpiCard testid="kpi-pipeline" variant="hero" accent="blue" label="Pipeline Value" value={money(k.pipeline_value)} sub="open deals" icon={Wallet} />
        <KpiCard testid="kpi-won" variant="hero" accent="green" label="Won Value" value={money(k.won_value)} sub="closed won" icon={Trophy} />
        <KpiCard testid="kpi-open-leads" label="Open Leads" value={k.open_leads} sub="in pipeline" icon={Target} accent="amber" />
        <KpiCard testid="kpi-projects" label="Active Projects" value={k.active_projects} sub="in delivery" icon={KanbanSquare} accent="violet" />
        <KpiCard testid="kpi-clients" label="Clients" value={k.clients} sub="accounts" icon={Building2} accent="primary" />
        <KpiCard testid="kpi-items" label="Open Work Items" value={k.open_items} sub="across projects" icon={ListTodo} accent="red" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Section title="Sales Funnel" className="lg:col-span-2" testid="dashboard-funnel">
          <div className="p-4" style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.funnel}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="stage" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="count" fill="#2563EB" radius={[6, 6, 0, 0]} maxBarSize={56} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>

        <Section title="Leads by Source" testid="dashboard-source">
          <div className="p-4" style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.by_source} dataKey="count" nameKey="source" cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2}>
                  {data.by_source.map((_, i) => <Cell key={i} fill={CHART[i % CHART.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-2 flex flex-wrap gap-2">
              {data.by_source.map((s, i) => (
                <span key={s.source} className="flex items-center gap-1.5 text-xs capitalize text-muted-foreground">
                  <span className="h-2 w-2 rounded-full" style={{ background: CHART[i % CHART.length] }} />{s.source}
                </span>
              ))}
            </div>
          </div>
        </Section>
      </div>

      <Section title="Project Health" className="mt-6" testid="dashboard-projects">
        <div className="divide-y divide-border">
          {data.projects.map((p) => (
            <div key={p.name} className="flex items-center gap-4 px-4 py-3">
              <span className={cn("h-2.5 w-2.5 rounded-full", HEALTH_DOT[p.health])} />
              <span className="w-64 truncate text-sm font-medium text-foreground">{p.name}</span>
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full bg-primary" style={{ width: `${p.progress}%` }} />
              </div>
              <span className="w-10 text-right text-xs font-medium text-muted-foreground">{p.progress}%</span>
              <StatusBadge status={p.status} />
            </div>
          ))}
        </div>
      </Section>

      <div className="mt-8 flex justify-center border-t border-border pt-6">
        <IrisBrand />
      </div>
    </div>
  );
}
